<script setup lang="ts">
import {computed, ref, watch, PropType} from "vue";
import DataExportDownloadMenu from "@/components/DataExportDownloadMenu.vue";
import {useReportingStore} from "@/store/reporting";
import {IUsageReportData} from "@/pages/Insights/ReportsDetailPage/reportTypes";

const reportingState = useReportingStore();

const props = defineProps({
  showInstitution: {
    type: String,
    required: false,
  },
  showCohort: {
    type: String,
    required: false,
  },
  reportData: {
    type: Array as PropType<IUsageReportData[]>,
    required: true,
  },
  loadingData: {
    type: Boolean,
    default: false,
  },
});

// Single filter value
const globalFilter = ref("");
const activeGroup = ref("tenant");

const emit = defineEmits<{
  (e: "changeGroup", tab: string): void,
  (e: "export-report", format: "excel" | "csv"): void,
}>();

watch(activeGroup, async (newGroup) => {
  emit("changeGroup", newGroup);
});

// Filtered data
const filteredData = computed(() => props.reportData.filter((item) => {
  const filterValue = globalFilter.value.toLowerCase();
  const matchesTenant = item.tenant?.title?.toLowerCase().includes(filterValue);
  const matchesInstitution = containsInstitutions.value
    ? item.institution?.title?.toLowerCase().includes(filterValue)
    : false;
  const matchesCohort = containsCohorts.value
    ? item.cohort?.title?.toLowerCase().includes(filterValue)
    : false;
  return matchesTenant || matchesInstitution || matchesCohort;
}));

const containsCohorts = computed(() => props.reportData.some((item) => item.cohort));

const containsInstitutions = computed(() => props.reportData.some((item) => item.institution));

// Headers
const headers = computed(() => {
  const headers = [
    {
      title: "Tenant",
      key: "tenant.title",
    },
  ];

  if (containsInstitutions.value)
    headers.push({
      title: "Institution",
      key: "institution.title",
    });

  if (containsCohorts.value)
    headers.push({
      title: "Cohort",
      key: "cohort.title",
    });

  headers.push(
    {
      title: "Total Licenses",
      key: "totalLicenses",
    },
    {
      title: "Total Users",
      key: "total",
    },
    {
      title: "Completed",
      key: "completed",
    },
    {
      title: "Incomplete",
      key: "pending",
    },
    {
      title: "Completion",
      key: "percentage",
    },
    {
      title: "Adoption",
      key: "adoption",
    },
  );

  return headers;
});

// Get color for usage percentage
function getColor(usagePercentage: number) {
  if (usagePercentage < 30) return "red";
  else if (usagePercentage < 50) return "orange";
  else return "green";
}

function handleExportReportClick(format: "excel" | "csv") {
  emit("export-report", format);
}
</script>

<template>
  <v-card variant="outlined" class="bg-white mt-4">
    <v-tabs>
      <v-tab
        v-if="!reportingState.isCohortFilteringActive && !reportingState.isInstitutionFilteringActive"
        value="tenant"
        :class="{ 'text-primary': activeGroup === 'tenant' }"
        @click="activeGroup = 'tenant'"
      >
        Tenant
      </v-tab>
      <v-tab
        v-if="!reportingState.isCohortFilteringActive"
        value="institution"
        :class="{ 'text-primary': activeGroup === 'institution' }"
        @click="activeGroup = 'institution'"
      >
        Institution
      </v-tab>
      <v-tab
        value="cohort"
        :class="{ 'text-primary': activeGroup === 'cohort' }"
        @click="activeGroup = 'cohort'"
      >
        Cohort
      </v-tab>
    </v-tabs>

    <v-row no-gutters>
      <v-col cols="12">

        <v-divider/>
        <v-row align="center" class="my-1 px-3">
          <v-spacer></v-spacer>
          <v-col cols="3">
            <v-text-field
              v-model="globalFilter"
              label="Filter by tenant, institution, cohort"
              :hide-details="true"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="auto">
            <data-export-download-menu :formats="['csv', 'excel']" @click="handleExportReportClick"
                                       class="mr-2"/>
          </v-col>
        </v-row>
        <v-divider/>

        <!-- Data Table -->
        <v-data-table
          v-if="filteredData.length > 0"
          :items="filteredData"
          :headers="headers"
          :loading="loadingData"
          :items-per-page="50"
          class="borderless"
        >
          <template v-slot:header.percentage="{ column }">
            <div class="d-flex align-center">
              {{ column.title }}
              <v-tooltip activator="parent" location="top">
                <template v-slot:activator="{ props }">
                  <v-icon color="grey" v-bind="props" class="ml-1" size="small">fa fa-info-circle</v-icon>
                </template>
                Percentage of signed up users who have completed the assessment.
              </v-tooltip>
            </div>
          </template>

          <template v-slot:header.adoption="{ column }">
            <div class="d-flex align-center">
              {{ column.title }}
              <v-tooltip activator="parent" location="top">
                <template v-slot:activator="{ props }">
                  <v-icon color="grey" v-bind="props" class="ml-1" size="small">fa fa-info-circle</v-icon>
                </template>
                Percentage of licenses that have been used and have completed the assessment.
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.percentage="{ item }">
            <v-chip :color="getColor(item.percentage as number)">
              {{ item.percentage }}%
            </v-chip>
          </template>

          <template v-slot:item.adoption="{ item }">
            <v-chip :color="getColor(item?.adoption as number)">
              {{ item?.adoption }}%
            </v-chip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped>
</style>
